import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/components/ui/use-toast";
import { Service } from '@/types/dashboard';

export interface UnclaimedBusinessProfile {
  id: string;
  business_name: string | null;
  bio: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  social_links: any | null;
  service_name: string | null;
  service_description: string | null;
  service_price: number | null;
  service_image: string | null;
  currency: string | null;
  profession: string | null;
  profile_picture: string | null;
  banner_image: string | null;
  service_provider_name: string | null;
  phone_number: string | null;
  service_page_link: string | null;
  created_at: string;
  updated_at: string;
}

export interface ServiceImage {
  id: string;
  service_id: string;
  image_url: string;
  created_at: string;
}

export interface UnclaimedService extends Service {
  images: ServiceImage[];
  selectedImageIndex: number;
}

export interface ServiceCategory {
  id: string;
  name: string;
  user_id: string;
}

export interface UseUnclaimedPageResult {
  profile: UnclaimedBusinessProfile | null;
  services: UnclaimedService[];
  categories: ServiceCategory[];
  isLoading: boolean;
  unclaimedPageNotFound: boolean;
  searchTerm: string;
  categoryId: string | null;
  setSearchTerm: (term: string) => void;
  setCategoryId: (id: string | null) => void;
  handleImageNavigation: (serviceId: string, direction: 'prev' | 'next') => void;
}

export function useUnclaimedPage(unclaimedId: string): UseUnclaimedPageResult {
  const [profile, setProfile] = useState<UnclaimedBusinessProfile | null>(null);
  const [services, setServices] = useState<UnclaimedService[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unclaimedPageNotFound, setUnclaimedPageNotFound] = useState(false);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  const fetchUnclaimedProfile = useCallback(async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('unclaimed')
        .select('*')
        .eq('id', unclaimedId)
        .maybeSingle();
      
      if (error || !data) {
        console.error('Error finding unclaimed profile:', error);
        setUnclaimedPageNotFound(true);
        return null;
      }
      
      return data as UnclaimedBusinessProfile;
    } catch (error) {
      console.error('Error in fetchUnclaimedProfile:', error);
      return null;
    }
  }, [unclaimedId]);

  const fetchUnclaimedServices = useCallback(async (servicePageLink: string) => {
    try {
      const { data, error } = await (supabase as any)
        .from('unclaimed')
        .select('*')
        .eq('service_page_link', servicePageLink)
        .order('service_price', { ascending: true });
      
      if (error) {
        console.error('Error finding unclaimed services:', error);
        return [];
      }
      
      return data as UnclaimedBusinessProfile[];
    } catch (error) {
      console.error('Error in fetchUnclaimedServices:', error);
      return [];
    }
  }, []);

  const handleImageNavigation = useCallback((serviceId: string, direction: 'prev' | 'next') => {
    setServices(prevServices => 
      prevServices.map(service => {
        if (service.id === serviceId && service.images.length > 1) {
          const currentIndex = service.selectedImageIndex;
          const maxIndex = service.images.length - 1;
          
          let newIndex;
          if (direction === 'next') {
            newIndex = currentIndex >= maxIndex ? 0 : currentIndex + 1;
          } else {
            newIndex = currentIndex <= 0 ? maxIndex : currentIndex - 1;
          }
          
          return { ...service, selectedImageIndex: newIndex };
        }
        return service;
      })
    );
  }, []);

  const processUnclaimedDataToServices = (unclaimedDataArray: UnclaimedBusinessProfile[]): UnclaimedService[] => {
    return unclaimedDataArray
      .filter(data => data.service_name) // Only include entries with service names
      .map(data => {
        const serviceImages: ServiceImage[] = data.service_image ? [{
          id: data.id,
          service_id: data.id,
          image_url: data.service_image,
          created_at: data.created_at
        }] : [];

        // Create a service from the unclaimed data that matches the Service interface
        const service: UnclaimedService = {
          id: data.id,
          name: data.service_name,
          description: data.service_description || '',
          price: data.service_price || 0,
          image_url: data.service_image || '',
          category_id: 'unclaimed',
          is_active: true,
          service_mode: 'online',
          travel_fee: 0,
          service_area: null,
          instant_booking: false,
          requires_scheduling: true,
          duration: null,
          duration_unit: null,
          discounted_price: null,
          created_at: data.created_at,
          updated_at: data.updated_at,
          images: serviceImages,
          selectedImageIndex: 0
        };
        
        return service;
      });
  };

  const loadInitialData = async () => {
    console.log('Loading unclaimed data for ID:', unclaimedId);
    setIsLoading(true);
    
    try {
      const unclaimedProfile = await fetchUnclaimedProfile();
      
      if (unclaimedProfile) {
        console.log('Fetched unclaimed profile:', unclaimedProfile);
        setProfile(unclaimedProfile);
        
        // Fetch all services for this business using the service_page_link
        const allUnclaimedServices = await fetchUnclaimedServices(unclaimedProfile.service_page_link || '');
        console.log('Fetched all unclaimed services:', allUnclaimedServices);
        
        // Convert all unclaimed data to services format
        const processedServices = processUnclaimedDataToServices(allUnclaimedServices);
        console.log('Processed services:', processedServices);
        setServices(processedServices);
        
        // Set categories (we can add a simple category based on profession)
        const defaultCategories: ServiceCategory[] = unclaimedProfile.profession ? 
          [{ id: 'default', name: unclaimedProfile.profession, user_id: unclaimedProfile.id }] : [];
        setCategories(defaultCategories);
      }
    } catch (error) {
      console.error('Error loading unclaimed data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load unclaimed business data"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (unclaimedId) {
      loadInitialData();
    }
  }, [unclaimedId]);

  return {
    profile,
    services,
    categories,
    isLoading,
    unclaimedPageNotFound,
    searchTerm,
    categoryId,
    setSearchTerm,
    setCategoryId,
    handleImageNavigation
  };
} 